/**
 * Маркеры изменений под осью X — дни, когда сущность меняли через OneAds.
 *
 * ★ Маркер ОДИН на день, независимо от числа правок. Точка на каждую
 * правку превратила бы ось в частокол в день массового редактирования, а
 * ответ на «что тут случилось» всё равно даёт список в тултипе.
 *
 * ★ День берётся по UTC — как и точки графика. Время внутри строки
 * показывается в поясе читателя, и это разные вещи: пояс отвечает на
 * «когда для меня», день — на «к какой точке линии это относится».
 * Сгруппируй по местному, и маркер уедет от своей точки.
 *
 * Изменения прямо в кабинете Apple Ads сюда не попадают: их нет в
 * `audit_log`, и показать их нечем. Так и в ТЗ.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLayer } from "@/lib/layer-stack";
import { createPortal } from "react-dom";

import { TIP_BASE_MAX_H, type TipBox, tooltipBox } from "@/lib/chart-marker-tip";

export type DayChange = {
  ts_utc: string;
  what: string;
  actor_kind: "user" | "rule" | "script";
  actor_label: string;
};

export type DayMarker = {
  day: string;
  changes: DayChange[];
};

/** Время в поясе читателя. Дата приписывается, только если она
 *  отличается от дня маркера: иначе строка повторяла бы заголовок. */
function timeLabel(iso: string, markerDay: string): string {
  const d = new Date(iso);
  const hhmm = d.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const localDay = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;
  if (localDay === markerDay) return hhmm;
  const short = d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  return `${hhmm} · ${short}`;
}

function dayLabel(day: string): string {
  const [y, m, d] = day.split("-").map(Number);
  return new Date(y, (m ?? 1) - 1, d).toLocaleDateString(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

/** ★ Задержка закрытия. Подсказка висит НИЖЕ маркера с зазором, а сам
 *  маркер — треугольник 10×8 px. Курсор, идущий к списку, покидает
 *  треугольник и пересекает зазор, где не наведён ни на что. Без
 *  задержки подсказка закрывалась там же — и прокрутить её было
 *  физически нечем, сколько бы `overflow: auto` ни стоял в стилях.
 *  Именно это и было причиной «нет возможности проскролить». */
const CLOSE_DELAY_MS = 140;

type Anchor = { x: number; top: number; bottom: number };

export function ChangeMarkers({
  markers,
  positionOf,
  padLeftPct,
  padRightPct,
}: {
  markers: DayMarker[];
  /** Доля 0..1 вдоль ПОЛЯ ГРАФИКА для дня; `null` — день вне окна.
   *  Считает вызывающий: только он знает, как дни легли на точки при
   *  выбранной агрегации. */
  positionOf: (day: string) => number | null;
  /** ★ Отступы поля внутри графика, в процентах ширины. Без них маркеры
   *  разъезжались с линией: полоса маркеров занимает всю ширину, а поле
   *  графика вписано с отступами 62 и 18-62 пикселя из 900 — почти семь
   *  процентов, то есть два дня на месячном отрезке. Маркер, указывающий
   *  не на тот день, хуже отсутствующего. */
  padLeftPct: number;
  padRightPct: number;
}) {
  const [open, setOpen] = useState<string | null>(null);
  useLayer(open !== null, () => setOpen(null));
  // ★ Координаты подсказки — ЭКРАННЫЕ, и рисуется она порталом в body.
  //
  // `position: absolute` её обрезало: подсказка висит НИЖЕ полосы маркеров,
  // а панель графика (`.ads-chart-slot`) ограничена по высоте и обрезает
  // содержимое. Никакой z-index этого не лечит — обрезка родителя сильнее.
  // Та же ловушка, что была у превью скриншотов CPP (§28).
  const [anchor, setAnchor] = useState<Anchor | null>(null);
  const [box, setBox] = useState<TipBox | null>(null);
  const tipRef = useRef<HTMLDivElement | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const plot = 100 - padLeftPct - padRightPct;

  const cancelClose = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  const closeSoon = useCallback(() => {
    cancelClose();
    closeTimer.current = setTimeout(() => {
      setOpen(null);
      setAnchor(null);
      setBox(null);
    }, CLOSE_DELAY_MS);
  }, [cancelClose]);

  useEffect(() => cancelClose, [cancelClose]);

  const show = (day: string, el: HTMLElement) => {
    cancelClose();
    const r = el.getBoundingClientRect();
    setAnchor({ x: r.left + r.width / 2, top: r.top, bottom: r.bottom });
    // Высота считается после отрисовки — до неё содержимое не измерено.
    // Пока не посчитана, показываем базовую: она же и была раньше.
    setBox(null);
    setOpen(day);
  };

  // ★ `useLayoutEffect`, а не `useEffect`: он выполняется ДО отрисовки, и
  // подсказка не успевает мигнуть на промежуточном месте.
  useLayoutEffect(() => {
    const el = tipRef.current;
    if (!el || !anchor) return;
    const next = tooltipBox({
      need: el.scrollHeight,
      anchorTop: anchor.top,
      anchorBottom: anchor.bottom,
      viewportH: window.innerHeight,
    });
    setBox((prev) =>
      prev && prev.top === next.top && prev.maxH === next.maxH && prev.side === next.side
        ? prev
        : next,
    );
  }, [anchor, open]);

  const marker = open ? markers.find((m) => m.day === open) : null;

  return (
    <div className="chart-markers">
      {markers.map((m) => {
        const frac = positionOf(m.day);
        // День вне текущего окна — например, период сменили, а маркеры
        // ещё старые. Рисовать его у края значило бы соврать о дате.
        if (frac == null) return null;
        const left = padLeftPct + frac * plot;
        return (
          <div
            key={m.day}
            className="chart-marker"
            style={{ left: `${left}%` }}
            onMouseEnter={(e) => show(m.day, e.currentTarget)}
            onMouseLeave={closeSoon}
            title={`${m.changes.length} change${m.changes.length === 1 ? "" : "s"}`}
          >
            <svg width="10" height="8" viewBox="0 0 10 8" aria-hidden>
              <polygon points="5 0 10 8 0 8" fill="var(--c-text-3)" />
            </svg>
          </div>
        );
      })}

      {/* Подсказка — ОДНА на всю полосу и снаружи цикла: так её события
          мыши не путаются с событиями маркера, под которым она открыта. */}
      {marker && anchor && createPortal(
        <div
          ref={tipRef}
          className="chart-marker-tip"
          style={{
            left: anchor.x,
            top: box ? box.top : anchor.bottom + 6,
            maxHeight: box ? box.maxH : TIP_BASE_MAX_H,
          }}
          onMouseEnter={cancelClose}
          onMouseLeave={closeSoon}
        >
          <div className="chart-marker-tip-head">{dayLabel(marker.day)}</div>
          {marker.changes.map((c, k) => (
            <div key={k} className="chart-marker-tip-row">
              <div className="chart-marker-tip-time">
                {timeLabel(c.ts_utc, marker.day)}
              </div>
              <div>{c.what}</div>
              <div className="chart-marker-tip-who">{c.actor_label}</div>
            </div>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}
